import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, map, switchMap, tap } from 'rxjs';
import { AppConfigService } from 'app/core/config/app-config.service';
import { AuthHttpService } from 'app/core/auth/auth-http-service';
import { UserRole } from 'app/core/auth/auth.repository';
import { PresenceService } from '@app/services/presence.service';
import { VirtualClassroomService } from '@app/services/vclassroom.service';
import { CompaniesService } from '@app/services/companies.service';

export interface SwitchProfile {
  account_id: number;
  name: string;
  email: string;
  role: number;
  company: string;
  is_owner: boolean;
  is_current: boolean;
}

export interface SwitchProfiles {
  can_manage: boolean;
  current_account_id: number;
  accounts: SwitchProfile[];
}

const EMPTY: SwitchProfiles = { can_manage: false, current_account_id: 0, accounts: [] };

/** Translation key of a numeric role, e.g. 3 -> 'role.courseadmin'. */
export function roleTranslationKey(role: number): string {
  return `role.${(UserRole[role] || 'user').toLowerCase()}`;
}

/** Saved accounts the signed-in person can switch between (avatar menu + user settings). */
@Injectable({ providedIn: 'root' })
export class SwitchProfilesService {
  private readonly _profiles = new BehaviorSubject<SwitchProfiles>(EMPTY);
  readonly profiles$ = this._profiles.asObservable();

  private get apiUrl() { return `${this.appConfig.get<string>('serverUrl', '')}/v1/account/switch-profiles`; }

  constructor(
    private http: HttpClient,
    private appConfig: AppConfigService,
    private authHttpService: AuthHttpService,
    private presenceService: PresenceService,
    private vclassService: VirtualClassroomService,
    private companiesService: CompaniesService,
  ) {}

  load(): Observable<SwitchProfiles> {
    return this.http.get<any>(this.apiUrl).pipe(
      map(res => (res?.data as SwitchProfiles) ?? EMPTY),
      tap(profiles => this._profiles.next(profiles)),
    );
  }

  add(email: string, password: string): Observable<SwitchProfiles> {
    return this.http.post<any>(this.apiUrl, { email, password }).pipe(switchMap(() => this.load()));
  }

  remove(accountId: number): Observable<SwitchProfiles> {
    return this.http.delete<any>(`${this.apiUrl}/${accountId}`).pipe(switchMap(() => this.load()));
  }

  /** Replaces the current session with the chosen account's session. */
  switchTo(accountId: number): Observable<void> {
    return this.http.post<any>(`${this.apiUrl}/${accountId}/switch`, {}).pipe(
      tap(res => {
        if (res?.status !== 'OK' || !res?.data?.access_token) {
          throw new Error(res?.message || 'Switch failed');
        }
        this.presenceService.stopPresence(true);
        this.vclassService.disconnectRoom();
        this.companiesService.clearSelection();
        this.authHttpService.applyLoginResponse(res);
        this._profiles.next(EMPTY);
      }),
      map(() => undefined),
    );
  }
}
